/**
 * OperatingHoursEditor — controlled 7-day hours editor used by
 * `LocationDetailsStep` in the onboarding wizard.
 *
 * Shape matches `RestaurantSchema.operatingHours`
 * (`Record<DayOfWeek, { open: 'HH:mm', close: 'HH:mm', closed: boolean }>`)
 * so the captured value can be passed through to
 * `restaurantService.createRestaurant` without a transform.
 *
 * The shared `DEFAULT_OPERATING_HOURS` and `DAYS_OF_WEEK` from
 * `@order-up/common` drive both this editor and the admin RestaurantModal —
 * no drift between the "first restaurant" path here and the "second
 * location" path in the admin app.
 */

import { DAYS_OF_WEEK, type DayOfWeek } from '@order-up/common';

export type DayHours = { open: string; close: string; closed: boolean };
export type OperatingHoursValue = Record<string, DayHours>;

export interface OperatingHoursEditorProps {
  readonly value: OperatingHoursValue;
  readonly onChange: (next: OperatingHoursValue) => void;
}

export function OperatingHoursEditor({ value, onChange }: OperatingHoursEditorProps) {
  const setDay = (day: DayOfWeek, patch: Partial<DayHours>) => {
    const current = value[day] ?? { open: '09:00', close: '22:00', closed: false };
    onChange({
      ...value,
      [day]: { ...current, ...patch },
    });
  };

  return (
    <div className="space-y-2">
      {DAYS_OF_WEEK.map(({ key, label }) => {
        const day = value[key] ?? { open: '09:00', close: '22:00', closed: false };
        return (
          <div
            key={key}
            className="grid grid-cols-[7rem,auto,1fr,auto,1fr] items-center gap-3 rounded-lg border border-white/5 bg-zinc-900/40 p-3"
          >
            <span className="text-sm font-medium text-zinc-200">{label}</span>

            <label className="flex items-center gap-2 text-xs text-zinc-400">
              <input
                type="checkbox"
                checked={day.closed}
                onChange={(e) => setDay(key as DayOfWeek, { closed: e.target.checked })}
                className="h-4 w-4 rounded border-white/10 bg-zinc-800"
              />
              Closed
            </label>

            <input
              type="time"
              value={day.open}
              onChange={(e) => setDay(key as DayOfWeek, { open: e.target.value })}
              disabled={day.closed}
              className="rounded-lg border border-border bg-input px-3 py-2 text-sm text-white disabled:opacity-40"
              aria-label={`${label} opening time`}
            />

            <span className="text-center text-xs text-zinc-500">to</span>

            <input
              type="time"
              value={day.close}
              onChange={(e) => setDay(key as DayOfWeek, { close: e.target.value })}
              disabled={day.closed}
              className="rounded-lg border border-border bg-input px-3 py-2 text-sm text-white disabled:opacity-40"
              aria-label={`${label} closing time`}
            />
          </div>
        );
      })}
    </div>
  );
}
